import type {
  PackagingComponent,
  PackagingRecyclabilityAssessmentStoredEntry,
  RecyclingStream,
} from '@/shared/hooks/api/packagingSuppliers'
import {
  isEn18120DfrAssessmentEntry,
  isLegacyRecyclabilityAssessmentEntry,
  isPaperRecyclabilityAssessmentEntry,
} from '@/shared/hooks/api/packagingSuppliers'
import type { ComponentEditorLabels } from '@/shared/components/packaging/componentEditorTypes'
import { getRecyclingStreamLabel } from '@/shared/components/packaging/componentEditorUtils'
import { resolveRecyclabilityGradeFromAssessment } from './resolveRecyclabilityGradeFromAssessment'

export type RecyclabilityAssessmentDocumentAnnex1ProseKey =
  | 'constituentsCollectionSorting'
  | 'constituentsRecycling'
  | 'constituentsNegativeInfluence'

export type RecyclabilityAssessmentDocumentAnnex1Prose = Record<
  RecyclabilityAssessmentDocumentAnnex1ProseKey,
  { en: string; zh: string }
>

export type RecyclabilityAssessmentDocumentAnnex1Values = {
  description: string
  weightAsPercentageOfFunctionalUnit: number
  referencedStandards: string | null
  intendedRecyclingStreamLabel: string
  componentRecyclingPercentage: number | null
  functionalUnitRecyclingPercentage: number | null
  totalRecyclingPercentage: number | null
}

function parseRecyclingPct(raw: string | undefined): number | null {
  if (raw == null) {
    return null
  }
  const trimmed = raw.trim().replace(/%$/, '')
  if (trimmed.length === 0) {
    return null
  }
  const value = Number(trimmed)
  if (!Number.isFinite(value)) {
    return null
  }
  return value
}

function dominantRecyclingStreamFromComponents(
  components: PackagingComponent[]
): RecyclingStream | null {
  let bestStream: RecyclingStream | null = null
  let bestWeight = -1
  for (const component of components) {
    const stream = component.recyclingStream ?? null
    if (stream == null || stream === 'NONE') {
      continue
    }
    if (component.weightInGrams > bestWeight) {
      bestWeight = component.weightInGrams
      bestStream = stream
    }
  }
  return bestStream
}

function targetedStreamFromAssessment(
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
): string | null {
  const latest = history?.[0]
  if (latest == null) {
    return null
  }
  if (isEn18120DfrAssessmentEntry(latest)) {
    const stream = latest.result.targetedStream?.trim()
    return stream != null && stream.length > 0 ? stream : null
  }
  if (isPaperRecyclabilityAssessmentEntry(latest)) {
    return latest.result.targetedRecyclingStream
  }
  if (isLegacyRecyclabilityAssessmentEntry(latest)) {
    return latest.targetedRecyclingStream
  }
  return null
}

function componentRecyclingPercentageFromBom(
  components: PackagingComponent[]
): number | null {
  const totalWeight = components.reduce(
    (sum, component) => sum + component.weightInGrams,
    0
  )
  if (totalWeight <= 0) {
    return null
  }
  let recyclableWeight = 0
  for (const component of components) {
    if (!component.recyclable) {
      continue
    }
    const pct = parseRecyclingPct(component.recyclingPct)
    if (pct == null) {
      recyclableWeight += component.weightInGrams
      continue
    }
    recyclableWeight += (component.weightInGrams * pct) / 100
  }
  return (recyclableWeight / totalWeight) * 100
}

export function resolveRecyclabilityAssessmentDocumentAnnex1Values(params: {
  itemName: string
  components: PackagingComponent[]
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
  labels: ComponentEditorLabels
}): RecyclabilityAssessmentDocumentAnnex1Values {
  const assessmentGrade = resolveRecyclabilityGradeFromAssessment(params.history)
  let componentRecyclingPercentage =
    assessmentGrade?.recyclableContentPct ?? null
  if (componentRecyclingPercentage == null) {
    componentRecyclingPercentage = componentRecyclingPercentageFromBom(
      params.components
    )
  }

  const targetedStream = targetedStreamFromAssessment(params.history)
  let intendedRecyclingStreamLabel = '—'
  if (targetedStream != null) {
    intendedRecyclingStreamLabel = targetedStream
  } else {
    const dominantStream = dominantRecyclingStreamFromComponents(
      params.components
    )
    intendedRecyclingStreamLabel = getRecyclingStreamLabel(
      dominantStream,
      params.labels
    )
  }

  const weightAsPercentageOfFunctionalUnit = 100
  let functionalUnitRecyclingPercentage: number | null = null
  if (componentRecyclingPercentage != null) {
    functionalUnitRecyclingPercentage =
      (componentRecyclingPercentage * weightAsPercentageOfFunctionalUnit) / 100
  }

  return {
    description: params.itemName.trim() || '—',
    weightAsPercentageOfFunctionalUnit,
    referencedStandards: null,
    intendedRecyclingStreamLabel,
    componentRecyclingPercentage,
    functionalUnitRecyclingPercentage,
    totalRecyclingPercentage: functionalUnitRecyclingPercentage,
  }
}

export function pickAnnex1ProseLocale(
  locale: string,
  bilingual: { en: string; zh: string }
): string {
  if (locale.startsWith('zh')) {
    return bilingual.zh
  }
  return bilingual.en
}

export function formatAnnex1Percentage(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return '—'
  }
  return `${value.toFixed(1)}%`
}
